import { useState } from "react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
  Check,
  X,
  Plus,
  Trash2,
  Lock,
  Unlock,
  Eye,
  SquarePen,
  FolderOpen,
  FolderLock,
  Network,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertDialog } from "@/widgets/AlertDailog";
import { PaginationControl } from "@/widgets/renderPagination";
import { useLocation } from "wouter";
import { Popup } from "@/widgets/RuleDetailDailog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { deleteWorkflowData, toggleActiveWorkflowStatus, toggleWorkflowStatus, useWorkflowRepository } from "@/hooks/useLocalStorage";
import { WorkflowAction } from "@/lib/api-services";
import { DeleteConfirmationDialog } from "@/widgets/DeleteModal";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { Input } from "../ui/input";
import { ConfirmationDialog } from "@/widgets/AlertCommon";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useBreadcrumbs } from "../BreadcrumbContext";
import { CreateWorkflow } from "./CreateWorkflow";
import { SearchDropdown } from "@/widgets/SearchDropdown";
import { CheckInOutDialog } from "./CheckInOutDialog";


interface TableHierarchicalNodeProps {
  topLevelData: WorkflowAction[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onEditForm: (data: any) => void;
}

function TableForm({
  topLevelData,
  pageIndex,
  pageSize,
  totalCount,
  onPageChange,
  onEditForm,
}: TableHierarchicalNodeProps) {
  const [currentData, setCurrentData] = useState<WorkflowAction[]>([]);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [reqStatus, setReqStatus] = React.useState(false);

  const [showPopup, setShowPopup] = React.useState(false);
  const [popupName, setPopupName] = React.useState("");
  const [headingData, setHeadingData] = React.useState<string | null>("");
  const [descriptionContent, setDescriptionContent] = React.useState<string | null>("");

  const [_location, setLocation] = useLocation();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<WorkflowAction | null>(null);

  const [toggleMode, setToggleMode] = useState<"checkinout" | "activate">("checkinout");

  const [checkInOutDialogOpen, setCheckInOutDialogOpen] = useState(false);
  const [itemToCheckInOut, setItemToCheckInOut] = useState<WorkflowAction | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [itemToToggle, setItemToToggle] = useState<WorkflowAction | null>(null);

  const deletePortalForm = deleteWorkflowData();
  const togglePublishMutation = toggleWorkflowStatus();
  const statusActiveMutation = toggleActiveWorkflowStatus();

  React.useEffect(() => {
    if (topLevelData) {
      setCurrentData(topLevelData);
    }
  }, [topLevelData]);


  function handleDeleteClick(e: React.MouseEvent, item: WorkflowAction) {
    e.stopPropagation();
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  }

  function handleConfirmDelete() {
    if (!itemToDelete) return;
    deletePortalForm.mutate(itemToDelete.PMId);
  }

  React.useEffect(() => {
    if (deletePortalForm.isSuccess) {
      setDeleteDialogOpen(false);
      setReqStatus(true);
      setAlertMessage("Workflow deleted successfully!");
      setItemToDelete(null);
      setAlertOpen(true);
      deletePortalForm.reset();
    } else if (deletePortalForm.isError) {
      console.log("deletePortalForm", deletePortalForm);

      const raw = deletePortalForm.error as any;
      const apiMessage = raw?.response?.data?.detail;

      setReqStatus(false);
      setDeleteDialogOpen(false);
      setAlertMessage(apiMessage || "Something went wrong");
      setAlertOpen(true);
      deletePortalForm.reset();
    }
  }, [deletePortalForm.isSuccess, deletePortalForm.isError]);


  function handleCheckInOutClick(e: React.MouseEvent, item: WorkflowAction) {
    e.stopPropagation();
    setItemToCheckInOut(item);
    setCheckInOutDialogOpen(true);
  }

  function handleConfirmCheckInOut(comment: string) {
    if (!itemToCheckInOut) return;
    togglePublishMutation.mutate({
      Id: itemToCheckInOut.PMId,
      data: {
        UserName: itemToCheckInOut.LastUser,
        Comment: comment,
      },
    });
  }

  function handleActivateClick(e: React.MouseEvent, item: WorkflowAction) {
    e.stopPropagation();
    setItemToToggle(item);
    setToggleMode("activate");
    setApproveDialogOpen(true);
  }

  function handleConfirmToggle() {
    if (!itemToToggle) return;
    statusActiveMutation.mutate(itemToToggle.PMId);
  }

  React.useEffect(() => {
    if (togglePublishMutation.isSuccess) {
      setCheckInOutDialogOpen(false);
      setReqStatus(true);
      const msg =
        itemToCheckInOut?.Status === 2 || itemToCheckInOut?.Status === 0
          ? "Workflow Checked In successfully!"
          : "Workflow Checked Out successfully!";
      setAlertMessage(msg);
      setItemToCheckInOut(null);
      setAlertOpen(true);
      togglePublishMutation.reset();
    } else if (togglePublishMutation.isError) {
      const raw = togglePublishMutation.error as Error;
      setReqStatus(false);
      setCheckInOutDialogOpen(false);
      setAlertMessage(parseApiErrorMessage(raw.message));
      setAlertOpen(true);
      togglePublishMutation.reset();
    }
  }, [togglePublishMutation.isSuccess, togglePublishMutation.isError]);

  React.useEffect(() => {
    if (statusActiveMutation.isSuccess) {
      setApproveDialogOpen(false);
      setReqStatus(true);
      setAlertMessage(
        itemToToggle?.isActive
          ? "Workflow Deactivated successfully!"
          : "Workflow Activated successfully!"
      );
      setItemToToggle(null);
      setAlertOpen(true);
      statusActiveMutation.reset();
    } else if (statusActiveMutation.isError) {
      const raw = statusActiveMutation.error as Error;
      setReqStatus(false);
      setApproveDialogOpen(false);
      setAlertMessage(parseApiErrorMessage(raw.message));
      setAlertOpen(true);
      statusActiveMutation.reset();
    }
  }, [statusActiveMutation.status]);


  function handleViewDescription(item: WorkflowAction) {
    setPopupName("Description");
    setHeadingData(item.WorkflowName)
    setDescriptionContent(item.Description);
    setShowPopup(true);
  }


  const confirmHeading =
    toggleMode === "checkinout"
      ? itemToToggle?.Status === 2
        ? "Confirm Check In"
        : "Confirm Check Out"
      : itemToToggle?.isActive
        ? "Confirm Deactivate"
        : "Confirm Activate";

  const confirmMessage =
    toggleMode === "checkinout"
      ? `Are you sure you want to ${itemToToggle?.Status === 2 ? "Check In" : "Check Out"} this workflow?`
      : `Are you sure you want to ${itemToToggle?.isActive ? "Deactivate" : "Activate"} this workflow?`;

  const confirmLabel =
    toggleMode === "checkinout"
      ? itemToToggle?.Status === 2
        ? "Check In"
        : "Check Out"
      : itemToToggle?.isActive
        ? "Deactivate"
        : "Activate";


  return (
    <div>
      <div className="border bg-white border-gray-100 rounded-lg p-2">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Parent ID</TableHead>
              <TableHead>WF Id</TableHead>
              <TableHead>Workflow Name</TableHead>
              <TableHead>Process Type</TableHead>
              <TableHead>WF Type</TableHead>
              <TableHead>Build</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Ver#</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Last User</TableHead>
              <TableHead>Created Time</TableHead>
              <TableHead>Last Modified</TableHead>
              <TableHead>Actions</TableHead>
              <TableHead>Version</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {currentData?.map((data) => (
              <TableRow key={data.PMId}>
                <TableCell className="">{data.PMId}</TableCell>
                <TableCell className="">{data.WFId}</TableCell>
                <TableCell className="">{data.WorkflowName}</TableCell>
                <TableCell className="">{data.ProcessType}</TableCell>
                <TableCell className="">{data.WorkFlowSubCategory_Name}</TableCell>

                <TableCell>
                  <button
                    disabled={data.Status === 1}
                    onClick={() => {
                      if (data?.PMId) {
                        window.open(
                          `https://workflow-designer.workfinity.com/workflow/${data.WFId}`,
                          "_blank"
                        );
                      }
                    }}
                    className="p-1 border border-black inline-flex items-center justify-center bg-white"
                  >
                    <Network
                      size={20}
                      strokeWidth={3.5}
                      className={`text-yellow-500 ${data.Status === 1 ? "opacity-50" : ""
                        }`}
                    />
                  </button>
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  <Button
                    variant="link"
                    onClick={() => handleViewDescription(data)}
                  >
                    View
                  </Button>
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  {data.Status === 0
                    ? "New Workflow"
                    : data.Status === 1
                      ? "Checked In"
                      : "Checked Out"}
                </TableCell>

                <TableCell className="max-w-xs truncate">{data.VersionNumber}</TableCell>

                <TableCell className="max-w-xs truncate">
                  {data.isActive ? (
                    <Check className="h-5 w-5 mr-1 text-green-800 font-bold stroke-[2.5]" />
                  ) : (
                    <X className="h-5 w-5 mr-1 text-red-800 font-bold stroke-[2.5]" />
                  )}
                </TableCell>

                <TableCell className="max-w-xs truncate">{data.LastUser}</TableCell>

                <TableCell className="max-w-xs">
                  <div className="flex flex-col">
                    {data.DateCreated ? (
                      <>
                        <span>
                          {new Date(data.DateCreated).toLocaleDateString("en-IN", {
                            day: "2-digit", month: "short", year: "numeric",
                          })}
                        </span>
                        <span className="text-xs text-black">
                          {new Date(data.DateCreated).toLocaleTimeString("en-IN", {
                            hour: "2-digit", minute: "2-digit",
                          })}
                        </span>
                      </>
                    ) : "-"}
                  </div>
                </TableCell>

                <TableCell className="max-w-xs">
                  <div className="flex flex-col">
                    {data.LastModified ? (
                      <>
                        <span>
                          {new Date(data.LastModified).toLocaleDateString("en-IN", {
                            day: "2-digit", month: "short", year: "numeric",
                          })}
                        </span>
                        <span className="text-xs text-black">
                          {new Date(data.LastModified).toLocaleTimeString("en-IN", {
                            hour: "2-digit", minute: "2-digit",
                          })}
                        </span>
                      </>
                    ) : "-"}
                  </div>
                </TableCell>

                <TableCell>
                  <div className="flex flex-row items-center space-x-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="w-10 h-10 p-0"
                        >
                          <Plus className="h-6 w-6 stroke-[2.5]" />
                        </Button>
                      </DropdownMenuTrigger>

                      <DropdownMenuContent align="end" className="min-w-[200px]">

                        <DropdownMenuItem
                          onClick={(e) => handleCheckInOutClick(e, data)}
                          className="flex items-center px-2 cursor-pointer"
                        >
                          {data.Status === 1 ? (
                            <FolderLock className="h-5 w-5 mr-2 text-purple-600 stroke-[2.5]" />
                          ) : (
                            <FolderOpen className="h-5 w-5 mr-2 text-sky-500 stroke-[2.5]" />
                          )}
                          <span className="text-md">
                            {data.Status === 1 ? "Check Out" : "Check In"}
                          </span>
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          className="flex items-center px-2 cursor-pointer"
                          onClick={() => onEditForm(data)}
                        >
                          <SquarePen className="h-5 w-5 mr-2 stroke-[2.5]" />
                          <span className="text-sm">Edit Workflow</span>
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          onClick={() => {
                            if (data?.WFId) {
                              window.open(
                                `https://workflow-designer.workfinity.com/workflow/${data.WFId}/preview`,
                                "_blank"
                              );
                            }
                          }}
                          className="flex items-center px-2 cursor-pointer"
                        >
                          <Eye className="h-5 w-5 mr-2 text-red-500 stroke-[2.5]" />
                          <span className="text-sm">View Workflow</span>
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          onClick={(e) => handleActivateClick(e, data)}
                          className="flex items-center px-2 cursor-pointer"
                        >
                          {data.isActive ? (
                            <Unlock className="h-5 w-5 mr-2 text-green-500 stroke-[2.5]" />
                          ) : (
                            <Lock className="h-5 w-5 mr-2 text-yellow-500 stroke-[2.5]" />
                          )}
                          <span className="text-sm">
                            {data.isActive ? "Deactivate" : "Activate"}
                          </span>
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          onClick={(e) => handleDeleteClick(e, data)}
                          className="flex items-center px-2 cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                        >
                          <Trash2 className="h-5 w-5 mr-2 stroke-[2.5]" />
                          <span className="text-sm">Delete</span>
                        </DropdownMenuItem>

                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  <Button
                    variant="link"
                    onClick={() => {
                      setLocation(`/workflowversion/${data.PMId}`);
                    }}
                  >
                    Version History
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="mt-2">
          <PaginationControl
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={onPageChange}
          />
        </div>
      </div>

      <AlertDialog
        open={alertOpen}
        onOpenChange={setAlertOpen}
        message={alertMessage}
        status={reqStatus}
      />

      {showPopup && (
        <Popup
          popupName={popupName}
          heading={headingData}
          content={descriptionContent}
          onClose={() => setShowPopup(false)}
        />
      )}

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        isPending={deletePortalForm.isPending}
        itemName={itemToDelete as any}
        itemType="Workflow"
        requireAuthCode={false}
      />

      <CheckInOutDialog
        open={checkInOutDialogOpen}
        onOpenChange={setCheckInOutDialogOpen}
        onConfirm={handleConfirmCheckInOut}
        isPending={togglePublishMutation.isPending}
        apiStatus={togglePublishMutation}
        workflowName={itemToCheckInOut?.WorkflowName}
        versionNumber={itemToCheckInOut?.VersionNumber}
        username={itemToCheckInOut?.LastUser}
        mode={itemToCheckInOut?.Status === 1 ? "checkout" : "checkin"}
      />

      <ConfirmationDialog
        open={approveDialogOpen}
        onOpenChange={setApproveDialogOpen}
        onConfirm={handleConfirmToggle}
        isPending={statusActiveMutation.isPending}
        heading={confirmHeading}
        message={confirmMessage}
        confirmLabel={confirmLabel}
      />
    </div>
  );
}


export function WorkflowEngine() {
  const { setBreadcrumbs } = useBreadcrumbs();

  const testdata = [
    {
      "PMId": 26,
      "WFId": 26,
      "WorkflowName": "New_copyrr",
      "ProcessType": "Workflow",
      "WorkflowType": "Candidate",
      "Description": "sadasd",
      "Status": 0,
      "VersionNumber": 0,
      "isActive": 0,
      "LastUser": "Max",
      "DateCreated": "2026-04-24T11:28:23.677000",
      "LastModified": null,
      "ExchangePartner": null,
      "WorkFlowSubCategory": 6058,
      "WorkFlowEntity": 5976,
      "ExchangePartner_Name": null,
      "WorkFlowSubCategory_Name": "Non-Commercial",
      "WorkFlowEntity_Name": "Weights and Measures Devices (Scales)"
    }
  ]

  const [topLevelTypes, setTopLevelTypes] = useState<WorkflowAction[]>([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [filterOpen, setFilterOpen] = useState(false);

  const [wfIdInput, setWfIdInput] = useState<string>("");
  const [wfNameInput, setWfNameInput] = useState<string>("");
  const [wfStatusInput, setWfStatusInput] = useState<string>("");

  const [appliedWfId, setAppliedWfId] = useState<string | undefined>();
  const [appliedWfName, setAppliedWfName] = useState<string | undefined>();
  const [appliedWfStatus, setAppliedWfStatus] = useState<string | undefined>();

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [editFormData, setEditFormData] = useState<any>(null);

  const { data: types, isLoading } = useWorkflowRepository(
    pageIndex,
    pageSize,
    appliedWfId,
    appliedWfName,
    appliedWfStatus
  );

  React.useEffect(() => {
    setBreadcrumbs([{ label: "Workflow", path: `/` }]);
  }, []);

  React.useEffect(() => {
    setTopLevelTypes(types?.items ?? []);
    // setTopLevelTypes(testdata);
    setTotalCount(types?.total ?? 0);
  }, [types]);

  const handleSearch = () => {
    setAppliedWfId(wfIdInput.trim() || undefined);
    setAppliedWfName(wfNameInput.trim() || undefined);
    setAppliedWfStatus(wfStatusInput || undefined);
    setPageIndex(1);
  };

  const handleRefresh = (close: boolean) => {
    setWfIdInput("");
    setWfNameInput("");
    setWfStatusInput("");
    setAppliedWfId(undefined);
    setAppliedWfName(undefined);
    setAppliedWfStatus(undefined);
    setPageIndex(1);
    if (close) setFilterOpen(false);
  };

  const handleAddForm = () => {
    setEditFormData(null);
    setOpenCreateDialog(true);
  };

  const handleEditForm = (data: any) => {
    setEditFormData(data);
    setOpenCreateDialog(true);
  };


  return (
    <div className=" min-h-full">

      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="min-w-0">
            <h2 className="text-lg md:text-xl font-semibold text-black">
              Manage WorkFlow
            </h2>
            <p className="text-black mt-1 text-sm md:text-base">
              Use workflow designer to create and manage application workflow including sub-processes
            </p>
          </div>

          <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
            <Button
              className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
              size="sm"
              onClick={() => setFilterOpen((prev) => !prev)}
            >
              Filter Workflow
            </Button>
            <Button
              className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
              size="sm"
              onClick={handleAddForm}
            >
              <Plus className="h-3 w-3 md:h-4 md:w-4 mr-2" />
              Add Workflow
            </Button>
          </div>
        </div>
      </div>

      {filterOpen && (
        <Card className="mb-4 md:mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row md:items-end md:space-x-4 space-y-3 md:space-y-0">
              <div className="flex-1">
                <label className="text-sm font-medium mb-1.5 block">Parent ID</label>
                <Input
                  placeholder="Enter Parent ID"
                  value={wfIdInput}
                  onChange={(e) => setWfIdInput(e.target.value)}
                />
              </div>

              <div className="flex-1">
                <label className="text-sm font-medium mb-1.5 block">Workflow Name</label>
                <SearchDropdown
                  type="Workflows"
                  value={wfNameInput}
                  onChange={setWfNameInput}
                  placeholder="Select Workflow Name"
                />
              </div>

              <div className="flex-1">
                <label className="text-sm font-medium mb-1.5 block">Status</label>
                <Select
                  value={wfStatusInput}
                  onValueChange={(value) => setWfStatusInput(value === "all" ? "" : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="0">New Workflow</SelectItem>
                    <SelectItem value="1">Checked In</SelectItem>
                    <SelectItem value="2">Checked Out</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4 flex justify-center space-x-2">
              <Button onClick={handleSearch}>Search</Button>
              <Button variant="outline" onClick={() => handleRefresh(false)}>
                Refresh
              </Button>
              <Button variant="outline" onClick={() => handleRefresh(true)}>
                Close
              </Button>
            </div>
          </CardContent>
        </Card>
      )}


      {!isLoading ? (
        <Card>
          <CardContent className="p-4 md:p-6">
            <div className="space-y-2">
              {topLevelTypes && topLevelTypes.length > 0 ? (
                <TableForm
                  topLevelData={topLevelTypes}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  onPageChange={setPageIndex}
                  onEditForm={handleEditForm}
                />
              ) : (
                <div className="text-center py-8 text-black">
                  <p>No Workflow found. Create one to get started.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="p-6 text-black">Loading...</div>
      )}

      {
        openCreateDialog &&
        <CreateWorkflow
          open={openCreateDialog}
          onOpenChange={setOpenCreateDialog}
          initialData={editFormData}
          offerData={null}
        />
      }

    </div>
  );
}